import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiSave, FiEye, FiX, FiCheck, FiPlus, FiTrash2, FiRotateCcw, FiArrowLeft,
  FiLayout, FiImage, FiFilm, FiType, FiSquare, FiSliders, FiDroplet,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { settingsAPI } from '../../services/api';
import { fetchTenantConfig, fetchBranding } from '../../store/slices/settingsSlice';
import { hasPermission } from '../../utils/permissions';
import LoginRenderer, { DEFAULT_LOGIN_PAGE } from '../../components/LoginRenderer';
import MultiLangInput from '../../components/MultiLangInput';
import ImageUploadField from '../../components/ImageUploadField';
import { getLocalized } from '../../i18n/getLocalized';
import { SUPPORTED_LANGUAGES, getLanguageMeta } from '../../i18n/supportedLanguages';
import './BrandingStudio.scss';

const ACCENT_PRESETS = [
  '#6366f1', '#3b82f6', '#0ea5e9', '#14b8a6', '#10b981',
  '#f59e0b', '#ef4444', '#ec4899', '#8b5cf6', '#0f172a',
];

function OptionGroup({ label, value, onChange, options, help }) {
  return (
    <div className="studio-field">
      <label className="studio-field__label">{label}</label>
      <div className="studio-options">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`studio-option ${value === o.value ? 'studio-option--active' : ''}`}
            onClick={() => onChange(o.value)}
          >
            {o.icon && <span className="studio-option__icon">{o.icon}</span>}
            <span>{o.label}</span>
          </button>
        ))}
      </div>
      {help && <span className="studio-field__help">{help}</span>}
    </div>
  );
}

function Toggle({ label, help, value, onChange }) {
  return (
    <label className="studio-toggle">
      <div className="studio-toggle__copy">
        <span className="studio-toggle__label">{label}</span>
        {help && <span className="studio-field__help">{help}</span>}
      </div>
      <span className={`studio-switch ${value ? 'studio-switch--on' : ''}`}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        <span className="studio-switch__thumb" />
      </span>
    </label>
  );
}

function ColorField({ label, value, onChange, help }) {
  return (
    <div className="studio-field">
      <label className="studio-field__label">{label}</label>
      <div className="studio-color">
        <label className="studio-color__native" style={{ background: value }}>
          <input type="color" value={value} onChange={(e) => onChange(e.target.value)} />
        </label>
        <input
          className="studio-color__hex"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
        />
      </div>
      {help && <span className="studio-field__help">{help}</span>}
    </div>
  );
}

function Section({ title, description, children }) {
  return (
    <section className="studio-section">
      <header className="studio-section__head">
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </header>
      <div className="studio-section__body">{children}</div>
    </section>
  );
}

function BrandingStudio() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const { config } = useSelector((s) => s.settings);
  const isAdmin = hasPermission(user?.role, 'manage_settings', user?.effectivePermissions);

  const [form, setForm] = useState({
    companyName: '', logoUrl: '', accentColor: '#6366f1',
    welcomeMessage: '', loginTagline: '', heroImageUrl: '',
  });
  const [loginPage, setLoginPage] = useState(DEFAULT_LOGIN_PAGE);
  const [activeSection, setActiveSection] = useState('identity');
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [dirty, setDirty] = useState(false);
  const tenantDefaultLang = config?.defaultLanguage || 'en';
  const [previewLang, setPreviewLang] = useState(tenantDefaultLang);

  useEffect(() => { setPreviewLang(tenantDefaultLang); }, [tenantDefaultLang]);

  useEffect(() => {
    if (!config) dispatch(fetchTenantConfig());
  }, [config, dispatch]);

  useEffect(() => {
    if (config?.branding) {
      setForm({
        companyName: config.branding.companyName || config.organization?.name || '',
        logoUrl: config.branding.logoUrl || '',
        accentColor: config.branding.accentColor || '#6366f1',
        welcomeMessage: config.branding.welcomeMessage || '',
        loginTagline: config.branding.loginTagline || '',
        heroImageUrl: config.branding.heroImageUrl || '',
      });
      setLoginPage({ ...DEFAULT_LOGIN_PAGE, ...(config.branding.loginPage || {}) });
      setDirty(false);
    }
  }, [config]);

  const updateForm = useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }, []);

  const updateLogin = useCallback((patch) => {
    setLoginPage((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateBranding({ ...form, loginPage });
      toast.success(t('brandingStudio.saveSuccess', { defaultValue: 'Login design saved' }));
      dispatch(fetchTenantConfig());
      dispatch(fetchBranding());
      setDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.error || t('brandingStudio.saveFailed', { defaultValue: 'Failed to save' }));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm(t('brandingStudio.confirmReset', { defaultValue: 'Reset login design to defaults? Brand identity (name, logo, accent) will be kept.' }))) {
      setLoginPage(DEFAULT_LOGIN_PAGE);
      setDirty(true);
    }
  };

  const previewBranding = useMemo(() => ({ ...form, loginPage }), [form, loginPage]);

  // Escape to close preview
  useEffect(() => {
    if (!showPreview) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShowPreview(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showPreview]);

  if (!isAdmin) {
    return (
      <div className="studio-denied">
        <h2>{t('brandingStudio.notAllowed', { defaultValue: 'Only tenant admins can access the Branding Studio.' })}</h2>
      </div>
    );
  }

  const sections = [
    { id: 'identity', label: t('brandingStudio.sectionIdentity', { defaultValue: 'Brand identity' }), icon: FiDroplet },
    { id: 'layout', label: t('brandingStudio.sectionLayout', { defaultValue: 'Layout' }), icon: FiLayout },
    { id: 'background', label: t('brandingStudio.sectionBackground', { defaultValue: 'Background' }), icon: FiImage },
    { id: 'slider', label: t('brandingStudio.sectionSlider', { defaultValue: 'Image slider' }), icon: FiFilm },
    { id: 'typography', label: t('brandingStudio.sectionTypography', { defaultValue: 'Typography & copy' }), icon: FiType },
    { id: 'controls', label: t('brandingStudio.sectionControls', { defaultValue: 'Inputs & buttons' }), icon: FiSquare },
    { id: 'visibility', label: t('brandingStudio.sectionVisibility', { defaultValue: 'Elements & visibility' }), icon: FiSliders },
  ];

  const slides = loginPage.slides || [];
  const features = loginPage.features || [];

  return (
    <div className="studio">
      <header className="studio__header">
        <div className="studio__header-left">
          <button type="button" className="studio__back" onClick={() => navigate('/settings')}>
            <FiArrowLeft />
          </button>
          <div>
            <div className="studio__eyebrow">{t('brandingStudio.eyebrow', { defaultValue: 'Branding Studio' })}</div>
            <h1 className="studio__title">{t('brandingStudio.title', { defaultValue: 'Design your login page' })}</h1>
          </div>
        </div>
        <div className="studio__header-actions">
          <div className="studio-lang-pick" title={t('brandingStudio.previewLang', { defaultValue: 'Preview language' })}>
            {SUPPORTED_LANGUAGES.map((code) => {
              const meta = getLanguageMeta(code);
              return (
                <button
                  key={code}
                  type="button"
                  className={`studio-lang-pick__opt ${previewLang === code ? 'studio-lang-pick__opt--active' : ''}`}
                  onClick={() => setPreviewLang(code)}
                  aria-pressed={previewLang === code}
                >
                  {meta.flag || code.toUpperCase()}
                </button>
              );
            })}
          </div>
          <button type="button" className="studio-btn studio-btn--ghost" onClick={handleReset}>
            <FiRotateCcw /> <span>{t('brandingStudio.reset', { defaultValue: 'Reset design' })}</span>
          </button>
          <button type="button" className="studio-btn studio-btn--secondary" onClick={() => setShowPreview(true)}>
            <FiEye /> <span>{t('brandingStudio.preview', { defaultValue: 'Preview' })}</span>
          </button>
          <button
            type="button"
            className="studio-btn studio-btn--primary"
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            <FiSave /> <span>{saving ? t('common.saving', { defaultValue: 'Saving…' }) : t('brandingStudio.save', { defaultValue: 'Save changes' })}</span>
          </button>
        </div>
      </header>

      <div className="studio__body">
        <aside className="studio__nav">
          {sections.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                type="button"
                className={`studio__nav-item ${activeSection === s.id ? 'studio__nav-item--active' : ''}`}
                onClick={() => setActiveSection(s.id)}
              >
                <Icon /> <span>{s.label}</span>
              </button>
            );
          })}
        </aside>

        <main className="studio__main">
          {activeSection === 'identity' && (
            <Section
              title={t('brandingStudio.identity.title', { defaultValue: 'Brand identity' })}
              description={t('brandingStudio.identity.desc', { defaultValue: 'Your company name, logo, accent color, and welcome copy.' })}
            >
              <div className="studio-grid studio-grid--2">
                <div className="studio-field">
                  <label className="studio-field__label">{t('settings.branding.companyName')}</label>
                  <MultiLangInput
                    value={form.companyName}
                    onChange={(v) => updateForm({ companyName: v })}
                    primaryLang={tenantDefaultLang}
                    placeholder={t('settings.branding.companyNamePlaceholder')}
                    inputClassName="studio-input"
                  />
                </div>
                <div className="studio-field">
                  <label className="studio-field__label">{t('settings.branding.logoUrl')}</label>
                  <ImageUploadField
                    kind="logo"
                    value={form.logoUrl}
                    onChange={(v) => updateForm({ logoUrl: v })}
                  />
                </div>
              </div>

              <div className="studio-field">
                <label className="studio-field__label">{t('settings.branding.accentColor')}</label>
                <div className="studio-accent">
                  <div className="studio-accent__swatches">
                    {ACCENT_PRESETS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={`studio-swatch ${form.accentColor === c ? 'studio-swatch--active' : ''}`}
                        style={{ background: c }}
                        onClick={() => updateForm({ accentColor: c })}
                        aria-label={`Color ${c}`}
                      >
                        {form.accentColor === c && <FiCheck />}
                      </button>
                    ))}
                  </div>
                  <div className="studio-color">
                    <label className="studio-color__native" style={{ background: form.accentColor }}>
                      <input type="color" value={form.accentColor} onChange={(e) => updateForm({ accentColor: e.target.value })} />
                    </label>
                    <input className="studio-color__hex" value={form.accentColor} onChange={(e) => updateForm({ accentColor: e.target.value })} />
                  </div>
                </div>
              </div>

              <div className="studio-field">
                <label className="studio-field__label">{t('settings.branding.welcomeMessage')}</label>
                <MultiLangInput
                  value={form.welcomeMessage}
                  onChange={(v) => updateForm({ welcomeMessage: v })}
                  primaryLang={tenantDefaultLang}
                  placeholder={t('settings.branding.welcomeMessagePlaceholder')}
                  inputClassName="studio-input"
                />
                <span className="studio-field__help">{t('settings.branding.welcomeMessageHelp')}</span>
              </div>
              <div className="studio-field">
                <label className="studio-field__label">{t('settings.branding.loginTagline')}</label>
                <MultiLangInput
                  value={form.loginTagline}
                  onChange={(v) => updateForm({ loginTagline: v })}
                  primaryLang={tenantDefaultLang}
                  placeholder={t('settings.branding.loginTaglinePlaceholder')}
                  multiline
                  rows={2}
                  inputClassName="studio-input"
                />
              </div>
              <div className="studio-field">
                <label className="studio-field__label">{t('settings.branding.heroImageUrl')}</label>
                <ImageUploadField
                  kind="hero"
                  value={typeof form.heroImageUrl === 'string' ? form.heroImageUrl : getLocalized(form.heroImageUrl, tenantDefaultLang)}
                  onChange={(v) => updateForm({ heroImageUrl: v })}
                  help={t('settings.branding.heroImageUrlHelp')}
                />
              </div>
            </Section>
          )}

          {activeSection === 'layout' && (
            <Section
              title={t('brandingStudio.layout.title', { defaultValue: 'Layout' })}
              description={t('brandingStudio.layout.desc', { defaultValue: 'Control the page structure, form position and theme.' })}
            >
              <OptionGroup
                label={t('brandingStudio.layoutTemplate', { defaultValue: 'Template' })}
                value={loginPage.layout}
                onChange={(v) => updateLogin({ layout: v })}
                options={[
                  { value: 'split', label: t('brandingStudio.layoutSplit', { defaultValue: 'Split — hero + form' }) },
                  { value: 'centered', label: t('brandingStudio.layoutCentered', { defaultValue: 'Centered card' }) },
                  { value: 'fullImage', label: t('brandingStudio.layoutFullImage', { defaultValue: 'Full background' }) },
                ]}
              />
              <OptionGroup
                label={t('brandingStudio.cardPosition', { defaultValue: 'Form position' })}
                value={loginPage.cardPosition}
                onChange={(v) => updateLogin({ cardPosition: v })}
                options={[
                  { value: 'left', label: t('brandingStudio.left', { defaultValue: 'Left' }) },
                  { value: 'center', label: t('brandingStudio.center', { defaultValue: 'Center' }) },
                  { value: 'right', label: t('brandingStudio.right', { defaultValue: 'Right' }) },
                ]}
              />
              <OptionGroup
                label={t('brandingStudio.cardStyle', { defaultValue: 'Card style' })}
                value={loginPage.cardStyle}
                onChange={(v) => updateLogin({ cardStyle: v })}
                options={[
                  { value: 'elevated', label: 'Elevated' },
                  { value: 'bordered', label: 'Bordered' },
                  { value: 'glass', label: 'Glass' },
                  { value: 'flat', label: 'Flat' },
                ]}
              />
              <OptionGroup
                label={t('brandingStudio.cardRadius', { defaultValue: 'Card corner radius' })}
                value={loginPage.cardRadius}
                onChange={(v) => updateLogin({ cardRadius: v })}
                options={[
                  { value: 'sharp', label: 'Sharp' },
                  { value: 'default', label: 'Default' },
                  { value: 'large', label: 'Large' },
                  { value: 'xl', label: 'Extra large' },
                ]}
              />
              <OptionGroup
                label={t('brandingStudio.theme', { defaultValue: 'Form theme' })}
                value={loginPage.theme}
                onChange={(v) => updateLogin({ theme: v })}
                options={[
                  { value: 'light', label: t('brandingStudio.light', { defaultValue: 'Light' }) },
                  { value: 'dark', label: t('brandingStudio.dark', { defaultValue: 'Dark' }) },
                ]}
              />
              <OptionGroup
                label={t('brandingStudio.logoSize', { defaultValue: 'Logo size' })}
                value={loginPage.logoSize}
                onChange={(v) => updateLogin({ logoSize: v })}
                options={[
                  { value: 'sm', label: t('brandingStudio.logoSm', { defaultValue: 'Small' }) },
                  { value: 'md', label: t('brandingStudio.logoMd', { defaultValue: 'Medium' }) },
                  { value: 'lg', label: t('brandingStudio.logoLg', { defaultValue: 'Large' }) },
                  { value: 'xl', label: t('brandingStudio.logoXl', { defaultValue: 'Extra large' }) },
                ]}
                help={t('brandingStudio.logoSizeHelp', { defaultValue: 'Controls the size of the logo mark and company name across the login page.' })}
              />
              <OptionGroup
                label={t('brandingStudio.topbarBackground', { defaultValue: 'Header background' })}
                value={loginPage.topbarBackground}
                onChange={(v) => updateLogin({ topbarBackground: v })}
                options={[
                  { value: 'surface', label: t('brandingStudio.topbarSurface', { defaultValue: 'Surface' }) },
                  { value: 'transparent', label: t('brandingStudio.topbarTransparent', { defaultValue: 'Transparent' }) },
                  { value: 'accent', label: t('brandingStudio.topbarAccent', { defaultValue: 'Accent' }) },
                  { value: 'custom', label: t('brandingStudio.topbarCustom', { defaultValue: 'Custom' }) },
                ]}
                help={t('brandingStudio.topbarHelp', { defaultValue: 'Applies to the centered and full-background layouts.' })}
              />
              {loginPage.topbarBackground === 'custom' && (
                <ColorField
                  label={t('brandingStudio.topbarCustomColor', { defaultValue: 'Header color' })}
                  value={loginPage.topbarCustomColor}
                  onChange={(v) => updateLogin({ topbarCustomColor: v })}
                />
              )}
            </Section>
          )}

          {activeSection === 'background' && (
            <Section
              title={t('brandingStudio.background.title', { defaultValue: 'Background' })}
              description={t('brandingStudio.background.desc', { defaultValue: 'Choose how the hero area looks. Use gradients, a hero image, or an animated mesh.' })}
            >
              <OptionGroup
                label={t('brandingStudio.backgroundStyle', { defaultValue: 'Background style' })}
                value={loginPage.backgroundStyle}
                onChange={(v) => updateLogin({ backgroundStyle: v })}
                options={[
                  { value: 'mesh', label: 'Animated mesh' },
                  { value: 'gradient', label: 'Gradient' },
                  { value: 'solid', label: 'Solid color' },
                  { value: 'image', label: 'Hero image' },
                  { value: 'slider', label: 'Slider' },
                ]}
              />
              {(loginPage.backgroundStyle === 'solid' || loginPage.backgroundStyle === 'mesh') && (
                <ColorField
                  label={t('brandingStudio.backgroundColor', { defaultValue: 'Base color' })}
                  value={loginPage.backgroundColor}
                  onChange={(v) => updateLogin({ backgroundColor: v })}
                />
              )}
              {(loginPage.backgroundStyle === 'gradient' || loginPage.backgroundStyle === 'mesh') && (
                <div className="studio-grid studio-grid--2">
                  <ColorField label={t('brandingStudio.gradientFrom', { defaultValue: 'Gradient from' })} value={loginPage.gradientFrom} onChange={(v) => updateLogin({ gradientFrom: v })} />
                  <ColorField label={t('brandingStudio.gradientTo', { defaultValue: 'Gradient to' })} value={loginPage.gradientTo} onChange={(v) => updateLogin({ gradientTo: v })} />
                </div>
              )}
              {loginPage.backgroundStyle === 'gradient' && (
                <div className="studio-field">
                  <label className="studio-field__label">{t('brandingStudio.gradientAngle', { defaultValue: 'Gradient angle' })} — {loginPage.gradientAngle}°</label>
                  <input type="range" min="0" max="360" step="1" value={loginPage.gradientAngle || 135} onChange={(e) => updateLogin({ gradientAngle: Number(e.target.value) })} />
                </div>
              )}
              {loginPage.backgroundStyle === 'image' && (
                <div className="studio-tip">
                  {t('brandingStudio.imageTip', { defaultValue: 'Set the Hero image URL in Brand identity.' })}
                </div>
              )}
              {loginPage.backgroundStyle === 'slider' && (
                <div className="studio-tip">
                  {t('brandingStudio.sliderTip', { defaultValue: 'Manage slides in the Image slider section.' })}
                </div>
              )}
            </Section>
          )}

          {activeSection === 'slider' && (
            <Section
              title={t('brandingStudio.slider.title', { defaultValue: 'Image slider' })}
              description={t('brandingStudio.slider.desc', { defaultValue: 'Create an image carousel for the hero side. Set Background style to "Slider" to display these.' })}
            >
              <div className="studio-grid studio-grid--2">
                <Toggle label={t('brandingStudio.sliderAutoplay', { defaultValue: 'Autoplay' })} value={loginPage.sliderAutoplay} onChange={(v) => updateLogin({ sliderAutoplay: v })} />
                <div className="studio-field">
                  <label className="studio-field__label">{t('brandingStudio.sliderInterval', { defaultValue: 'Interval (ms)' })}</label>
                  <input className="studio-input" type="number" min="2000" max="30000" step="500" value={loginPage.sliderInterval} onChange={(e) => updateLogin({ sliderInterval: Number(e.target.value) })} />
                </div>
              </div>
              <OptionGroup
                label={t('brandingStudio.sliderTransition', { defaultValue: 'Transition' })}
                value={loginPage.sliderTransition}
                onChange={(v) => updateLogin({ sliderTransition: v })}
                options={[
                  { value: 'fade', label: 'Fade' },
                  { value: 'slide', label: 'Slide' },
                  { value: 'zoom', label: 'Zoom' },
                ]}
              />

              <div className="studio-slides">
                {slides.map((slide, idx) => (
                  <div key={idx} className="studio-slide">
                    <div className="studio-slide__head">
                      <strong>{t('brandingStudio.slide', { defaultValue: 'Slide' })} {idx + 1}</strong>
                      <button type="button" className="studio-icon-btn" onClick={() => {
                        const next = slides.filter((_, i) => i !== idx);
                        updateLogin({ slides: next });
                      }}>
                        <FiTrash2 />
                      </button>
                    </div>
                    <div className="studio-field">
                      <label className="studio-field__label">{t('brandingStudio.slideImage', { defaultValue: 'Image URL' })}</label>
                      <ImageUploadField
                        kind="hero"
                        value={typeof slide.imageUrl === 'string' ? slide.imageUrl : getLocalized(slide.imageUrl, tenantDefaultLang)}
                        onChange={(v) => {
                          const next = [...slides]; next[idx] = { ...next[idx], imageUrl: v };
                          updateLogin({ slides: next });
                        }}
                      />
                    </div>
                    <div className="studio-grid studio-grid--2">
                      <div className="studio-field">
                        <label className="studio-field__label">{t('brandingStudio.slideTitle', { defaultValue: 'Caption title' })}</label>
                        <MultiLangInput
                          value={slide.title || ''}
                          onChange={(v) => {
                            const next = [...slides]; next[idx] = { ...next[idx], title: v };
                            updateLogin({ slides: next });
                          }}
                          primaryLang={tenantDefaultLang}
                          inputClassName="studio-input"
                        />
                      </div>
                      <div className="studio-field">
                        <label className="studio-field__label">{t('brandingStudio.slideSubtitle', { defaultValue: 'Caption subtitle' })}</label>
                        <MultiLangInput
                          value={slide.subtitle || ''}
                          onChange={(v) => {
                            const next = [...slides]; next[idx] = { ...next[idx], subtitle: v };
                            updateLogin({ slides: next });
                          }}
                          primaryLang={tenantDefaultLang}
                          inputClassName="studio-input"
                        />
                      </div>
                    </div>
                    <div className="studio-field">
                      <label className="studio-field__label">{t('brandingStudio.overlay', { defaultValue: 'Overlay darkness' })} — {Math.round((slide.overlayOpacity ?? 0.55) * 100)}%</label>
                      <input type="range" min="0" max="1" step="0.05" value={slide.overlayOpacity ?? 0.55} onChange={(e) => {
                        const next = [...slides]; next[idx] = { ...next[idx], overlayOpacity: Number(e.target.value) };
                        updateLogin({ slides: next });
                      }} />
                    </div>
                  </div>
                ))}
                <button type="button" className="studio-add" onClick={() => updateLogin({
                  slides: [...slides, { imageUrl: '', title: '', subtitle: '', overlayOpacity: 0.55 }],
                  backgroundStyle: 'slider',
                })}>
                  <FiPlus /> {t('brandingStudio.addSlide', { defaultValue: 'Add slide' })}
                </button>
              </div>
            </Section>
          )}

          {activeSection === 'typography' && (
            <Section
              title={t('brandingStudio.typography.title', { defaultValue: 'Typography & copy' })}
              description={t('brandingStudio.typography.desc', { defaultValue: 'Fine-tune fonts, heading weight, and feature bullets.' })}
            >
              <OptionGroup
                label={t('brandingStudio.fontFamily', { defaultValue: 'Font family' })}
                value={loginPage.fontFamily}
                onChange={(v) => updateLogin({ fontFamily: v })}
                options={[
                  { value: 'Inter', label: 'Inter' },
                  { value: 'Poppins', label: 'Poppins' },
                  { value: 'Lora', label: 'Lora' },
                  { value: 'Playfair Display', label: 'Playfair' },
                  { value: 'JetBrains Mono', label: 'JetBrains Mono' },
                  { value: 'System', label: 'System' },
                ]}
              />
              <OptionGroup
                label={t('brandingStudio.headingWeight', { defaultValue: 'Heading weight' })}
                value={loginPage.headingWeight}
                onChange={(v) => updateLogin({ headingWeight: v })}
                options={[
                  { value: '500', label: 'Medium' },
                  { value: '600', label: 'Semibold' },
                  { value: '700', label: 'Bold' },
                  { value: '800', label: 'Extrabold' },
                  { value: '900', label: 'Black' },
                ]}
              />
              <div className="studio-field">
                <label className="studio-field__label">{t('brandingStudio.features', { defaultValue: 'Feature bullets' })}</label>
                <div className="studio-features">
                  {features.map((f, idx) => (
                    <div key={idx} className="studio-features__row">
                      <MultiLangInput
                        value={f}
                        onChange={(v) => {
                          const next = [...features]; next[idx] = v; updateLogin({ features: next });
                        }}
                        primaryLang={tenantDefaultLang}
                        placeholder="e.g., End-to-end contract lifecycle"
                        inputClassName="studio-input"
                      />
                      <button type="button" className="studio-icon-btn" onClick={() => {
                        updateLogin({ features: features.filter((_, i) => i !== idx) });
                      }}>
                        <FiTrash2 />
                      </button>
                    </div>
                  ))}
                  <button type="button" className="studio-add studio-add--sm" onClick={() => updateLogin({ features: [...features, ''] })}>
                    <FiPlus /> {t('brandingStudio.addFeature', { defaultValue: 'Add bullet' })}
                  </button>
                </div>
                <span className="studio-field__help">{t('brandingStudio.featuresHelp', { defaultValue: 'Leave empty to use default bullets.' })}</span>
              </div>
            </Section>
          )}

          {activeSection === 'controls' && (
            <Section
              title={t('brandingStudio.controls.title', { defaultValue: 'Inputs & buttons' })}
              description={t('brandingStudio.controls.desc', { defaultValue: 'Shape of the form elements your users interact with.' })}
            >
              <OptionGroup label={t('brandingStudio.buttonStyle', { defaultValue: 'Button style' })} value={loginPage.buttonStyle} onChange={(v) => updateLogin({ buttonStyle: v })}
                options={[
                  { value: 'solid', label: 'Solid' },
                  { value: 'gradient', label: 'Gradient' },
                  { value: 'outline', label: 'Outline' },
                  { value: 'glass', label: 'Glass' },
                ]} />
              <OptionGroup label={t('brandingStudio.buttonRadius', { defaultValue: 'Button radius' })} value={loginPage.buttonRadius} onChange={(v) => updateLogin({ buttonRadius: v })}
                options={[
                  { value: 'sharp', label: 'Sharp' },
                  { value: 'default', label: 'Default' },
                  { value: 'pill', label: 'Pill' },
                ]} />
              <OptionGroup label={t('brandingStudio.inputStyle', { defaultValue: 'Input style' })} value={loginPage.inputStyle} onChange={(v) => updateLogin({ inputStyle: v })}
                options={[
                  { value: 'default', label: 'Default' },
                  { value: 'filled', label: 'Filled' },
                  { value: 'underlined', label: 'Underlined' },
                ]} />
            </Section>
          )}

          {activeSection === 'visibility' && (
            <Section
              title={t('brandingStudio.visibility.title', { defaultValue: 'Elements & visibility' })}
              description={t('brandingStudio.visibility.desc', { defaultValue: 'Toggle optional elements on or off.' })}
            >
              <Toggle label={t('brandingStudio.showLogo', { defaultValue: 'Show logo' })} value={loginPage.showLogo} onChange={(v) => updateLogin({ showLogo: v })} />
              <Toggle label={t('brandingStudio.showFeatures', { defaultValue: 'Show feature bullets' })} value={loginPage.showFeatures} onChange={(v) => updateLogin({ showFeatures: v })} />
              <Toggle label={t('brandingStudio.showTagline', { defaultValue: 'Show tagline' })} value={loginPage.showTagline} onChange={(v) => updateLogin({ showTagline: v })} />
              <Toggle label={t('brandingStudio.showPoweredBy', { defaultValue: 'Show "Powered by" footer' })} value={loginPage.showPoweredBy} onChange={(v) => updateLogin({ showPoweredBy: v })} />
              <Toggle label={t('brandingStudio.showTopbar', { defaultValue: 'Show top bar (non-split layouts)' })} value={loginPage.showTopbar} onChange={(v) => updateLogin({ showTopbar: v })} />
            </Section>
          )}
        </main>
      </div>

      {showPreview && (
        <div className="studio-preview-overlay" role="dialog" aria-label="Login page preview">
          <button
            type="button"
            className="studio-preview-exit"
            onClick={() => setShowPreview(false)}
            aria-label="Exit preview"
          >
            <FiX />
            <span>{t('brandingStudio.exitPreview', { defaultValue: 'Exit preview' })}</span>
          </button>
          <div className="studio-preview-stage">
            <LoginRenderer
              branding={previewBranding}
              currentLanguage={previewLang}
              defaultLanguage={config?.defaultLanguage || 'en'}
              email="demo@example.com"
              password="••••••••"
              previewMode
              labels={{
                defaultFeatures: [
                  t('auth.feature1'),
                  t('auth.feature2'),
                  t('auth.feature3'),
                  t('auth.feature4'),
                ],
                defaultHeadline: t('auth.modernContractManagement'),
                defaultTagline: t('auth.defaultTagline'),
                poweredBy: t('common.poweredBy'),
                welcomeBack: t('auth.welcomeBack'),
                signInTo: t('auth.signInTo', { company: form.companyName || '' }),
                emailAddress: t('auth.emailAddress'),
                emailPlaceholder: t('auth.emailPlaceholder'),
                password: t('auth.password'),
                passwordPlaceholder: t('auth.passwordPlaceholder'),
                signIn: t('auth.signIn'),
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default BrandingStudio;
